import React, { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { getRequest, staticOptions } from 'src/lib/core/api-client'
import { apiURI, constants } from 'src/lib/utils/constants'
import useSWR from 'swr'
import { Windmill, X } from '@phosphor-icons/react'
import { useLocale } from 'src/lib/hooks/useLocal'
import { Button, IconButton } from '@shopflo/ui'
import useNavigation from 'src/lib/hooks/useNavigation'
import Cookies from 'js-cookie'
import { MarketingCardBackendType, parseMarketingCard } from 'src/lib/utils/marketting-card'
import useGTM from 'src/lib/hooks/useGTM'

interface MarketingCardProps {
  show: boolean
}

const MarketingCard: React.FC<MarketingCardProps> = ({ show }) => {
  const { t } = useLocale()
  const {
    state: { sidebarOpen }
  } = useNavigation()
  const { sendGTMTrigger } = useGTM()
  const { data: markettingCard } = useSWR<MarketingCardBackendType>(
    `/cards`,
    async (url: string) => {
      const response: any = await getRequest(url, apiURI.FLO_KRATOS_CHECKOUT)
      return (response?.data?.[0]?.card_payload as MarketingCardBackendType) ?? null
    },
    staticOptions
  )

  const popupObject = useMemo(() => parseMarketingCard(markettingCard), [markettingCard])
  const [dismissed, setDismissed] = useState(() => Cookies.get(constants.MARKETING_POPUP_COOKIE) ?? false)

  const handleDismiss = () => {
    sendGTMTrigger('marketing_card_dismissed', 'click')
    Cookies.set(constants.MARKETING_POPUP_COOKIE, 'true')
    setDismissed(true)
  }

  if (!markettingCard || !sidebarOpen || dismissed) {
    return null
  }

  const renderImageContent = () => {
    if (popupObject.media.type === 'image') {
      return (
        <img src={popupObject.media.url} alt='' className='h-full w-full shadow-[0_0_60px_56px_#fafafa] object-fit' />
      )
    }

    return (
      <iframe
        src={popupObject.media.url}
        title='YouTube video player'
        allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
        allowFullScreen
        className='rounded-lg -z-10 h-full w-full'
      ></iframe>
    )
  }

  return (
    <AnimatePresence>
      {show && sidebarOpen && (
        <motion.div
          className='px-2 mb-2 mt-auto'
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ease: 'easeOut', duration: 0.3 }}
        >
          <div className='min-h-2' />
          <div className='w-full flex flex-col rounded-lg overflow-hidden relative bg-zinc-50 border-1 border-zinc-500/15'>
            {popupObject.isDismissable && (
              <IconButton
                size='xs'
                variant='ghost'
                className='absolute right-2 top-2 z-10 text-zinc-400'
                onClick={handleDismiss}
                IconComponent={X}
              />
            )}
            <div className='w-[252px] h-24 shadow-[0_0_60px_56px_#fafafa]'>{renderImageContent()}</div>
            <div className='px-4 py-3 flex flex-col gap-1.5'>
              <div className='flex flex-col gap-0.5'>
                <div className='flex gap-0.5 items-center'>
                  <Windmill className='text-teal-600' size={14} weight='fill' />
                  <span className='text-text-xs-semibold text-teal-600'>{t('whats_new')}</span>
                </div>
                <h3 className='text-text-xs-semibold text-zinc-800'>{popupObject.headerText}</h3>
              </div>
              <p className='text-text-xs-regular text-zinc-500'>{popupObject.subText}</p>
            </div>
            <div className='w-full px-2 pb-2'>
              <Button
                variant='outline'
                size='sm'
                className='w-full'
                onClick={() => {
                  sendGTMTrigger('marketing_card_cta_clicked', 'click')
                  window.open(popupObject.ctaLink, '_blank')
                }}
              >
                {popupObject.ctaTitle}
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default MarketingCard
